import { useState, useEffect } from "react";
import { PartnerPicker } from "../components/PartnerPicker";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Home, Loader2, Zap, Grid3x3, Ruler } from "lucide-react";
import { toast } from "sonner";
import RoofMap from "../components/RoofMap";
import FreeDownloadGate from "../components/FreeDownloadGate";

const num = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { maximumFractionDigits: 1 }));

export default function FreeTetto() {
  const [step, setStep] = useState(0);
  const [selfToken, setSelfToken] = useState(null);
  const [form, setForm] = useState({ address: "", citta: "", provincia: "", cap: "" });
  const [busy, setBusy] = useState(false);
  const [roof, setRoof] = useState(null);
  // Selezione da Google Places: compila automaticamente indirizzo, città, provincia e CAP.
  const onAddressSelect = (addr, place) => {
    const comps = place?.address_components || [];
    const get = (type) => comps.find((c) => c.types.includes(type));
    const city = get("locality")?.long_name || get("administrative_area_level_3")?.long_name || get("postal_town")?.long_name || "";
    const prov = (get("administrative_area_level_2")?.short_name || "").toUpperCase();
    const cap = get("postal_code")?.long_name || "";
    const full = place?.formatted_address || addr || "";
    setForm((f) => ({ ...f, address: full || f.address, citta: city || f.citta, provincia: prov || f.provincia, cap: cap || f.cap }));
  };
  const [client, setClient] = useState(null);
  const [copyClient, setCopyClient] = useState(false);
  const [partner, setPartner] = useState({ via_partner: false, partner_id: "", partner_name: "", partner_logo_url: "" });
  const reteMode = new URLSearchParams(window.location.search).get("rete") === "1";
  const isChiaviInMano = new URLSearchParams(window.location.search).get("flow") === "chiaviinmano";

  useEffect(() => {
    const s = new URLSearchParams(window.location.search).get("self");
    if (s) setSelfToken(s);
    if (s && reteMode) {
      api.get(`/public/self/${s}/state`).then(({ data }) => {
        if (data && (data.address || data.city)) {
          setClient(data);
          if (isChiaviInMano) {
            setForm((f) => ({ ...f, address: data.address || f.address, citta: data.city || f.citta, provincia: data.provincia || f.provincia }));
            setStep(1);
          }
        }
      }).catch(() => {});
    }
  }, []);

  const toggleCopyClient = (checked) => {
    setCopyClient(checked);
    if (checked && client) setForm((f) => ({ ...f, citta: client.city || f.citta, provincia: client.provincia || f.provincia, address: client.address || f.address }));
  };

  const start = async () => {
    if (!form.citta.trim() && !form.address.trim()) return toast.error("Inserisci almeno città o indirizzo");
    setBusy(true);
    try {
      const { data } = await api.post("/public/free/start", { tool: "tetto", self_token: selfToken || undefined, ...form, ...partner });
      setSelfToken(data.self_token); setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };

  const inc = roof?.panel_layout;
  const flat = roof?.flat_layout;
  const hasLayout = (inc?.enabled && inc?.summary?.count != null) || (flat?.compatible && flat?.summary?.count != null);
  const pl = (flat?.compatible && flat?.summary) ? flat.summary : (inc?.summary || {});

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="free-tetto-page">
      <header className="border-b border-border px-6 py-4 flex items-center gap-3">
        <Home className="h-6 w-6 text-primary" />
        <div>
          <div className="text-lg font-bold leading-none">Dimensionamento sul tetto</div>
          <div className="text-xs text-muted-foreground mt-0.5">Disegna il tuo tetto e scopri quanti pannelli puoi installare</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {step === 0 && (
          <Card className="p-6 rounded border-border max-w-2xl mx-auto" data-testid="free-step-address">
            <h2 className="text-base font-semibold mb-1">Dove si trova il tetto?</h2>
            <p className="text-sm text-muted-foreground mb-4">Centriamo la mappa satellitare sul tuo immobile.</p>
            {!reteMode && <div className="mb-5 pb-5 border-b border-border"><PartnerPicker value={partner} onChange={setPartner} /></div>}
            <div>
              <Label className="text-xs">Indirizzo completo, civico, città *</Label>
              <AddressAutocomplete value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v, citta: "", provincia: "", cap: "" }))} onSelect={onAddressSelect} placeholder="Es. Via Roma 12, Brescia" data-testid="free-address" />
              <p className="text-[11px] text-muted-foreground mt-1.5">Digita e <b>seleziona l'indirizzo dai suggerimenti</b>: rileviamo automaticamente città, provincia e CAP.</p>
            </div>
            {client && (
              <label className="mt-3 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm cursor-pointer" data-testid="free-copy-client">
                <input type="checkbox" checked={copyClient} onChange={(e) => toggleCopyClient(e.target.checked)} data-testid="free-copy-client-checkbox" className="h-4 w-4" />
                <span>Usa i dati del cliente{client.name ? <> <b>{client.name}</b></> : null}: l'indirizzo dell'immobile coincide con quello del cantiere.</span>
              </label>
            )}
            <Button onClick={start} disabled={busy} className="mt-4 gap-2" data-testid="free-start-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Inizia</Button>
          </Card>
        )}

        {step === 1 && selfToken && (
          <div data-testid="free-step-roof">
            <h2 className="text-base font-semibold mb-1">Disegna le falde del tuo tetto</h2>
            <p className="text-sm text-muted-foreground mb-4">Tocca i vertici sulla mappa per tracciare la falda, poi genera il layout dei pannelli.</p>
            <RoofMap
              lead={{ id: null, roof: null, address: form.address, city: form.citta, comune: form.citta, provincia: form.provincia }}
              selfToken={selfToken}
              rete={reteMode}
              aestheticPanel={!reteMode}
              onUpdate={(l) => setRoof(l?.roof || null)}
            />
            {hasLayout && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                <Card className="p-4 rounded border-border" data-testid="roof-stat-panels"><div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Grid3x3 className="h-3.5 w-3.5" /> Pannelli</div><div className="text-2xl font-bold text-primary">{pl.count}</div></Card>
                <Card className="p-4 rounded border-border"><div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Zap className="h-3.5 w-3.5" /> Potenza</div><div className="text-2xl font-bold">{num(pl.kwp)} kWp</div></Card>
                <Card className="p-4 rounded border-border"><div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Ruler className="h-3.5 w-3.5" /> Sup. utile</div><div className="text-2xl font-bold">{num(roof?.summary?.netta_m2)} m²</div></Card>
                <Card className="p-4 rounded border-border"><div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-muted-foreground mb-1"><Home className="h-3.5 w-3.5" /> Falde</div><div className="text-2xl font-bold">{(roof?.falde || []).length}</div></Card>
              </div>
            )}
            <Card className="p-6 rounded border-primary/40 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
              <div>
                <div className="font-semibold">{isChiaviInMano ? "Genera la relazione Chiavi in mano" : "Ricevi il dimensionamento completo"}</div>
                <div className="text-sm text-muted-foreground">{hasLayout ? (isChiaviInMano ? "La relazione unirà l'analisi della bolletta e il dimensionamento del tetto in un unico PDF." : "Te lo inviamo via email e lo apri subito online, con la possibilità di prenotare un consulente.") : "Disegna almeno una falda e genera il layout per ricevere la consulenza."}</div>
              </div>
              <FreeDownloadGate selfToken={selfToken} disabled={!hasLayout} label={isChiaviInMano ? "Genera relazione chiavi in mano" : "Scarica il PDF della consulenza"} />
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
